import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { Modal } from 'antd'
import { GoogleMap, Marker, Polygon } from '@react-google-maps/api'
import {
  ContainerPontosColeta,
  ControleLateral,
  ModalMalhaGeral,
  CardFlutuanteAlteracoes,
  CardFlutuanteInfo,
  SidebarContainerFullscreen,
  SidebarHeader,
  SidebarAlterations,
  SidebarContent,
  SidebarFooter,
  SidebarTopBar,
} from './styled'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faCompress, faDownload, faExpand, faFloppyDisk, faWarning, faXmark } from '@fortawesome/free-solid-svg-icons'
import { calculoAreaCoord } from '../../../../../../../../../../../../services/Afins'
import { ControleLateralModal } from './components/controleLateralModal'
import { Switcher } from '../../../../../../../../../../../../Components/CustomUi/Switcher'
import ButtonCustom from '../../../../../../../../../../../../Components/ButtonCustom'
import { errorAlert, warningAlert } from '../../../../../../../../../../../../utils/alert'
import { PropsAnalisesCadastradas } from '../../../../../../../..'
import { LabAnaliseItens } from '../../../../../../../../../../../../services/LabAnalise'
import { Render } from '../../../../../../../../../../../../Components/CustomUi/render'
import { ToastContainer } from 'react-toastify'
import { CardFullscreen } from './components/cardFullScreen'

// --- NOVA TIPAGEM PLANA (Sem geometry/properties) ---
export interface GeoPointFeature {
  INDICE: number
  NOME: number | string
  LATITUDE: number
  LONGITUDE: number
  COR?: string | null
  id?: string | number
  [key: string]: any
}

// As propriedades específicas que ficam dentro de "properties"
export interface MalhaProperties {
  INDICE: string
  NOME: string
  COMENTARIO: string | null
  ICONE: string | null
  ALTITUDE: string | null
  DATA: string | null
  ANGULO: string | null
  DISPLAY: string | null
  ZONA: string | null
  LATITUDE: string
  LONGITUDE: string
}

// A estrutura de cada item dentro do array "features"
export interface MalhaFeature {
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: [string, string] // [longitude, latitude] como string conforme seu JSON
  }
  properties: MalhaProperties
  color: string
}

// O objeto principal (FeatureCollection)
export interface MalhaGeoJson {
  type: 'FeatureCollection'
  features: MalhaFeature[]
  fileName: string
}

interface MapModalProps {
  isOpen: boolean
  onClose: () => void
  talhaoGeoJson: any
  initialPoints: GeoPointFeature[] | any
  tipo_analise_selecionada: string
  setTipo_analise_selecionada: React.Dispatch<React.SetStateAction<string>>
  tipo_analise_selecionada_id: number
  setTipo_analise_selecionada_id: React.Dispatch<React.SetStateAction<number>>
  pontos_selecionados: GeoPointFeature[]
  setPontos_selecionados: React.Dispatch<React.SetStateAction<GeoPointFeature[]>>
  selecionarTodos: boolean
  setSelecionarTodos: React.Dispatch<React.SetStateAction<boolean>>
  pontos_selecionados_adicionados: PropsAnalisesCadastradas[]
  setPontos_selecionados_adicionados: React.Dispatch<
    React.SetStateAction<PropsAnalisesCadastradas[]>
  >
  malhaShp: MalhaGeoJson[]
  setMalhaShp: React.Dispatch<React.SetStateAction<MalhaGeoJson[]>>
  grupoAnalises: LabAnaliseItens[]
  pontosTotalMalha: number
  id: string | number
  cor: string
  setCor: React.Dispatch<React.SetStateAction<string>>
  grupoAnalisesTratado: any[]
  salvarPontosSelecionados: Function
}

// --- Estilos ---
// Agora height 100% pois será controlado pelo pai
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  // borderRadius: '8px', // Controlado pelo wrapper se necessário
  overflow: 'hidden',
}

const talhaoOptions = {
  fillColor: '#14b9c5',
  fillOpacity: 0.25,
  strokeColor: '#00e1ff',
  strokeOpacity: 1,
  strokeWeight: 2,
  clickable: false,
  draggable: false,
  editable: false,
  zIndex: 1,
}

export const ModalNovaAnalise: React.FC<MapModalProps> = ({
  isOpen,
  onClose,
  talhaoGeoJson,
  initialPoints,
  tipo_analise_selecionada,
  setTipo_analise_selecionada,
  tipo_analise_selecionada_id,
  setTipo_analise_selecionada_id,
  pontos_selecionados,
  setPontos_selecionados,
  pontos_selecionados_adicionados,
  setPontos_selecionados_adicionados,
  selecionarTodos,
  setSelecionarTodos,
  malhaShp,
  setMalhaShp,
  grupoAnalises,
  salvarPontosSelecionados,
  pontosTotalMalha,
  id,
  cor,
  setCor,
  grupoAnalisesTratado,
}) => {
  const [points, setPoints] = useState<GeoPointFeature[]>([])

  const [inserirPontos, setInserirPontos] = useState<boolean>(false)
  const [moverPontos, setMoverPontos] = useState<boolean>(false)
  const [fake_loading, setFake_loading] = useState<boolean>(false)
  const [fullscreen, setFullscreen] = useState<boolean>(false)
  const [mapReady, setMapReady] = useState(false)
  const mapRef = useRef<google.maps.Map | null>(null)
  const polygonInstanceRef = useRef<google.maps.Polygon | null>(null)
  const fitBoundsDone = useRef(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  // 1. Carrega os pontos iniciais
  useEffect(() => {
    if (initialPoints && Array.isArray(initialPoints) && !id) {
      setPoints(initialPoints)
    }
  }, [initialPoints])

  useEffect(() => {
    if (!isOpen) {
      fitBoundsDone.current = false
      setFullscreen(false)
    }
  }, [isOpen, JSON.stringify(talhaoGeoJson)])

  // --- FULLSCREEN CHANGE LISTENER ---
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFull = !!document.fullscreenElement
      setFullscreen(isFull)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange) // Safari/Chrome antigo
    document.addEventListener('mozfullscreenchange', handleFullscreenChange) // Firefox antigo
    document.addEventListener('msfullscreenchange', handleFullscreenChange) // IE/Edge antigo

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange)
      document.removeEventListener('msfullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Função para alternar fullscreen no wrapper
  const toggleFullscreen = useCallback(() => {
    if (!wrapperRef.current) return

    if (!document.fullscreenElement) {
      // Entrar em fullscreen
      if (wrapperRef.current.requestFullscreen) {
        wrapperRef.current.requestFullscreen()
      } else if ((wrapperRef.current as any).webkitRequestFullscreen) {
        (wrapperRef.current as any).webkitRequestFullscreen()
      }
    } else {
      // Sair de fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen()
      }
    }
  }, [tipo_analise_selecionada]) // Dependência necessária para validação

  const defaultCenter = useMemo(() => ({ lat: -15, lng: -47 }), [])

  const talhaoPath = useMemo(() => {
    if (!talhaoGeoJson) return []
    return talhaoGeoJson
  }, [JSON.stringify(talhaoGeoJson)])

  const fitBoundsMap = (mapInstance: google.maps.Map) => {
    if (!mapInstance || fitBoundsDone.current) return

    if (talhaoPath.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      talhaoPath.forEach((coord: any) => bounds.extend(coord))
      mapInstance.fitBounds(bounds)
      fitBoundsDone.current = true
    } else if (points.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      let hasValidPoint = false
      points.forEach((pt) => {
        const latVal = Number(pt.LATITUDE)
        const lngVal = Number(pt.LONGITUDE)

        if (!isNaN(latVal) && !isNaN(lngVal) && latVal !== 0 && lngVal !== 0) {
          bounds.extend({ lat: latVal, lng: lngVal })
          hasValidPoint = true
        }
      })

      if (hasValidPoint && !bounds.isEmpty()) {
        mapInstance.fitBounds(bounds)
        fitBoundsDone.current = true
      }
    }
  }

  useEffect(() => {
    if (isOpen && mapRef.current && !fitBoundsDone.current) {
      // Pequeno delay para garantir que o mapa esteja renderizado
      const timer = setTimeout(() => {
        fitBoundsMap(mapRef.current!)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isOpen, talhaoPath, points])

  // Handlers
  const onPolygonLoad = useCallback((polygon: google.maps.Polygon) => {
    polygonInstanceRef.current = polygon
  }, [])

  const adicionarNovoPontoMalha = useCallback(
    (event: google.maps.MapMouseEvent) => {
      // 1. Verificações de segurança
      if (!inserirPontos || !event.latLng || !polygonInstanceRef.current) return

      const isInside = google.maps.geometry.poly.containsLocation(
        event.latLng,
        polygonInstanceRef.current
      )

      if (!isInside) {
        warningAlert('O ponto deve ser inserido dentro do talhão!', undefined, undefined, 'point-inside-talhao')
        return
      }

      const lat = event.latLng.lat()
      const lng = event.latLng.lng()

      let novoIndiceNum = 1 // Começa com 1 se a lista estiver vazia
      //@ts-ignore
      const novoNome = (malhaShp.features.length + 1).toString()
      // @ts-ignore
      const features = malhaShp?.features

      if (features && features.length > 0) {
        // Pega o último objeto do array
        const ultimoObjeto = features[features.length - 1]

        const ultimoIndiceVal = parseInt(ultimoObjeto?.properties?.INDICE)

        // Se for um número válido, soma 1
        if (!isNaN(ultimoIndiceVal)) {
          novoIndiceNum = ultimoIndiceVal + 1
        }
      }

      const novoIndiceStr = novoIndiceNum.toString()

      // @ts-ignore
      const newFeature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [lng.toString(), lat.toString()],
        },
        properties: {
          INDICE: novoIndiceStr, // Agora sequencial (ex: "392")
          NOME: novoNome, // Geralmente o Nome segue o Indice
          COMENTARIO: null,
          ICONE: null,
          ALTITUDE: null,
          DATA: null,
          ANGULO: null,
          DISPLAY: null,
          ZONA: null,
          LATITUDE: lat.toString(),
          LONGITUDE: lng.toString(),
        },
        color: '',
      }

      setMalhaShp((prev: any) => ({
        ...prev,
        features: [...prev.features, newFeature],
      }))

      const newPoint: GeoPointFeature = {
        id: novoIndiceNum,
        NOME: novoNome,
        INDICE: novoIndiceNum,
        LATITUDE: event.latLng.lat(),
        LONGITUDE: event.latLng.lng(),
        COR: null
      }
      setPoints((prev) => [...prev, newPoint])
    },
    // IMPORTANTE: Adicione malhaShp nas dependências para ler o estado atualizado
    // @ts-ignore
    [inserirPontos, malhaShp]
  )

  const moverPontosMalha = (index: number, event: google.maps.MapMouseEvent) => {
    if (!event.latLng || !polygonInstanceRef.current) return

    const isInside = google.maps.geometry.poly.containsLocation(
      event.latLng,
      polygonInstanceRef.current
    )

    if (!isInside) {
      errorAlert('Não é permitido mover o ponto para fora do talhão.')
      setPoints((prev) => [...prev])
      return
    }

    const newLng = event.latLng.lng()
    const newLat = event.latLng.lat()

    setPoints((prev) => {
      const newPoints = [...prev]
      newPoints[index] = {
        ...newPoints[index],
        LATITUDE: newLat,
        LONGITUDE: newLng,
      }
      return newPoints
    })

    setPontos_selecionados((prev) => {
      const pointId = points[index].NOME
      const isSelected = prev.some((p) => p.NOME === pointId)

      if (isSelected) {
        return prev.map((p) => {
          if (p.NOME === pointId) {
            return {
              ...p,
              LATITUDE: newLat,
              LONGITUDE: newLng,
            }
          }
          return p
        })
      }
      return prev
    })
  }

  const selecionarPontosMalha = useCallback(
    (index: number) => {
      if (inserirPontos)
        return warningAlert('Desative o inserir pontos para selecionar o ponto desejado', undefined, undefined, 'disable-insert-points')

      const targetPoint = points[index]
      const pointId = targetPoint.NOME

      const isSelected = pontos_selecionados.some((p) => p.NOME === pointId)

      if (isSelected) {
        setPontos_selecionados((prev) => prev.filter((p) => p.NOME !== pointId))

        setPoints((prev) => {
          const newPoints = [...prev]
          newPoints[index] = {
            ...newPoints[index],
            COR: null,
          }
          return newPoints
        })
      } else {
        if (!tipo_analise_selecionada) {
          return warningAlert('Selecione uma análise antes de marcar um ponto.', undefined, undefined, 'select-analysis-before-mark')
        }

        if (pontos_selecionados.length > 0) {
          let corDaSelecaoExistente = pontos_selecionados[0].COR

          if (cor !== corDaSelecaoExistente) {
            return warningAlert(
              'Você não pode misturar análises na mesma seleção. Salve ou limpe os pontos atuais antes de mudar de análise.',
              undefined,
              undefined,
              'mix-analysis-selection'
            )
          }
        }

        const pointWithColor = {
          ...targetPoint,
          COR: cor,
        }

        setPontos_selecionados((prev) => [...prev, pointWithColor])

        setPoints((prev) => {
          const newPoints = [...prev]
          newPoints[index] = pointWithColor
          return newPoints
        })
      }
    },
    [inserirPontos, cor, tipo_analise_selecionada, points, pontos_selecionados]
  )

  function adicionarPontosSelecionados() {
    if (pontos_selecionados.length === 0) {
      warningAlert('Selecione pontos no mapa antes de adicionar.', undefined, undefined, 'select-points-before-add')
      return
    }

    const corDosPontos = pontos_selecionados[0].COR
    if (cor !== corDosPontos) {
      return warningAlert(
        'A análise selecionada é diferente da análise dos pontos marcados. Selecione a análise correta ou limpe a seleção.',
        undefined,
        undefined,
        'analysis-mismatch'
      )
    }

    const corJaCadastrada = pontos_selecionados_adicionados.some((item) => item?.cor === cor)
    if (corJaCadastrada) {
      return warningAlert(
        'Já existe um grupo de pontos adicionado com esta análise. Por favor, escolha outra análise.',
        undefined,
        undefined,
        'group-color-exists'
      )
    }

    const profundidade = grupoAnalises?.filter(
      (item) => item?.cod_analise === tipo_analise_selecionada_id
    )

    const filtrar = grupoAnalises
      ?.filter((item) => item?.cod_analise === tipo_analise_selecionada_id)
      .map((item) => item?.valor)

    const pontos_adicionar = {
      id: Date.now(),
      cod_analise: tipo_analise_selecionada_id,
      pontos_selecionados: pontos_selecionados,
      profundidade: profundidade[0]?.profundidade_nome,
      analise_nome: profundidade[0]?.analise_nome,
      valor_analise: (Number(filtrar[0]) * pontos_selecionados?.length).toLocaleString('pt-br', {
        style: 'currency',
        currency: 'BRL',
      }),
      valor: filtrar[0],
      cor: cor,
    }

    setPontos_selecionados_adicionados((prev) => [...prev, pontos_adicionar])

    setPoints((prev) =>
      prev.map((p) => {
        const estavaSelecionado = pontos_selecionados.some((sel) => sel.NOME === p.NOME)
        if (estavaSelecionado) {
          return {
            ...p,
            COR: null,
          }
        }
        return p
      })
    )

    setPontos_selecionados([])
    setSelecionarTodos(false)
  }

  const selecionarTodosPontos = useCallback(
    (checked: boolean) => {

      if (tipo_analise_selecionada_id) {
        setSelecionarTodos(checked)
        if (checked) {
          const corParaAplicar = cor || '#ffffff'

          setPoints((prevPoints) => {
            return prevPoints.map((p) => ({
              ...p,
              COR: corParaAplicar,
            }))
          })

          setPontos_selecionados(() => {
            return points.map((p) => ({
              ...p,
              COR: corParaAplicar,
            }))
          })
        } else {
          setPoints((prevPoints) => {
            return prevPoints.map((p) => ({
              ...p,
              COR: null,
            }))
          })

          setPontos_selecionados([])
        }
      } else {
        warningAlert('Selecione uma análise no menu lateral antes de selecionar todos os pontos.', undefined, undefined, 'select-analysis-all-points')
      }
    },
    [cor, points]
  )

  function deletarPontosSelecionados(cod) {
    setFake_loading(true)
    setTimeout(() => {
      setPontos_selecionados_adicionados((prev) => prev.filter((item) => item?.id !== cod))
      setFake_loading(false)
    }, 500)
  }

  useEffect(() => {
    if (isOpen && initialPoints && Array.isArray(initialPoints)) {
      if (id && pontos_selecionados?.length > 0) {
        const selecionadosSet = new Set(pontos_selecionados.map((p) => String(p.INDICE)))
        const updated = initialPoints.map((ponto) => ({
          ...ponto,
          COR: selecionadosSet.has(String(ponto.INDICE)) ? cor : null,
        }))
        setPoints(updated)
      } else {
        setPoints(initialPoints)
      }
    }
  }, [isOpen])

  // --- Renderização dos markers ---
  const renderMarkers = () => {
    if (!tipo_analise_selecionada) return null
    return points.map((pt, index) => {
      const lng = Number(pt.LONGITUDE)
      const lat = Number(pt.LATITUDE)
      const labelText = pt.NOME ? pt.NOME.toString() : (index + 1).toString()
      const markerKey = `${pt.NOME || pt.id || index}-${lat}-${lng}`

      return (
        <Marker
          key={markerKey}
          position={{ lat, lng }}
          draggable={moverPontos}
          onDragEnd={(e) => moverPontosMalha(index, e)}
          onClick={() => selecionarPontosMalha(index)}
          label={{
            text: labelText,
            color: pt.COR ? 'white' : '#7b7b7b',
            fontWeight: 'bold',
            fontSize: '10px',
          }}
          icon={{
            path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
            fillColor: pt.COR || '#ffffff',
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: '#2c2c2c',
            scale: 1.5,
            anchor: new google.maps.Point(12, 22),
            labelOrigin: new google.maps.Point(12, 9),
          }}
        />
      )
    })
  }

  // --- Card flutuante de Habilitar alterações (Normal View) ---
  const renderCardAlteracoes = () => (
    <CardFlutuanteAlteracoes style={{ top: 80, left: 15 }}>
      <label
        style={{ fontSize: '12px', fontWeight: 700, color: '#7b7b7b', margin: 0, padding: 0 }}
      >
        Habilitar alterações
      </label>
      <Switcher
        label='Inserir pontos'
        isChecked={inserirPontos}
        trigger={(checked) => {
          setInserirPontos(checked)
          if (checked) setMoverPontos(false)
        }}
      />
      <Switcher
        label='Mover pontos'
        isChecked={moverPontos}
        trigger={(checked) => {
          setMoverPontos(checked)
          if (checked) setInserirPontos(false)
        }}
      />
    </CardFlutuanteAlteracoes>
  )

  const renderOverlayAnalise = () => {
    if (tipo_analise_selecionada || fullscreen) return null
    return (
      <div className='map__overlay'>
        <FontAwesomeIcon icon={faWarning} />
        Selecione o tipo de análise | profundidade para mostrar os pontos
      </div>
    )
  }
  // ========================
  // RENDERIZAÇÃO
  // ========================
  return (
    <ModalMalhaGeral
      title={
        <div className='title'>
          <h3>Nova análise</h3>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={'100%'}
      centered
      destroyOnClose
      footer={
        // Footer normal: apenas botão Salvar (Sair? talvez Fechar seja no X)
        // O usuário pediu "Salvar e Fechar" dentro do fullscreen.
        !fullscreen ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px',
            }}
          >
            <div /> {/* Espaçador */}
            <ButtonCustom
              onClick={salvarPontosSelecionados}
              icon={<FontAwesomeIcon icon={faFloppyDisk} />}
            >
              Salvar
            </ButtonCustom>
          </div>
        ) : null
      }
      bodyStyle={{ padding: 0 }}
    // bodyStyle={{ padding: 0 }} // Removendo duplicado
    >
      <ContainerPontosColeta>

        {/* Sidebar no lado esquerdo (apenas se NÃO fullscreen) */}
        {!fullscreen && (
          <ControleLateralModal
            inserirPontos={inserirPontos}
            setInserirPontos={setInserirPontos}
            moverPontos={moverPontos}
            setMoverPontos={setMoverPontos}
            tipo_analise_selecionada={tipo_analise_selecionada}
            setTipo_analise_selecionada={setTipo_analise_selecionada}
            cor_selecionada={cor}
            setCor_selecionada={setCor}
            setTipo_analise_selecionada_id={setTipo_analise_selecionada_id}
            selecionarTodos={selecionarTodos}
            grupoAnalises={grupoAnalises}
            talhao_area={String(calculoAreaCoord([talhaoPath]))}
            pontos_totais={pontosTotalMalha}
            pontos={pontos_selecionados}
            pontos_selecionados_adicionados={pontos_selecionados_adicionados}
            fake_loading={fake_loading}
            grupoAnalisesTratado={grupoAnalisesTratado}
            adicionarPontosSelecionados={adicionarPontosSelecionados}
            selecionarTodosPontos={selecionarTodosPontos}
            deletarPontosSelecionados={deletarPontosSelecionados}
          />
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

          {/* Wrapper principal do Mapa */}
          <div
            ref={wrapperRef}
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              borderRadius: fullscreen ? 0 : '8px',
              backgroundColor: 'white'
            }}
          >
            {fullscreen && <ToastContainer style={{ zIndex: 999999999 }} />}
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={14}
              center={talhaoPath?.[0] || defaultCenter}
              onLoad={(map) => {
                mapRef.current = map
                fitBoundsMap(map)
              }}
              onClick={adicionarNovoPontoMalha}
              options={{
                mapTypeId: 'satellite',
                fullscreenControl: false,
                zoomControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                draggableCursor: inserirPontos ? 'crosshair' : 'grab',
              }}
            >
              {talhaoPath?.length > 0 && (
                <Polygon onLoad={onPolygonLoad} paths={talhaoPath} options={talhaoOptions} />
              )}
              {renderMarkers()}

              {/* === ELEMENTOS INTERNOS === */}

              {/* 0. Card com Informações (Topo) - Apenas quando não está em fullscreen */}
              <Render condition={!fullscreen}>
                <CardFlutuanteInfo style={{ top: 15, left: 15, right: 15, width: 'auto' }}>
                  <div>
                    <label>Área do talhão</label>
                    <p>{calculoAreaCoord([talhaoPath])} ha</p>
                  </div>
                  <div>
                    <label>Grid amostral</label>
                    <p>
                      {points.length > 0
                        ? (calculoAreaCoord([talhaoPath]) / points.length).toFixed(2)
                        : 0}{' '}
                      pontos/ha
                    </p>
                  </div>
                  <div>
                    <label>Quantidade de pontos</label>
                    <p>{points.length}</p>
                  </div>

                  {/* Botão de tela cheia dentro do mapa */}
                  <div style={{ flexDirection: 'row', gap: 10 }}>
                    {!fullscreen && (
                      <ButtonCustom
                        onClick={toggleFullscreen}
                        type='tertiary'
                        icon={<FontAwesomeIcon icon={faExpand} />}
                      >
                        Tela cheia
                      </ButtonCustom>
                    )}
                  </div>
                </CardFlutuanteInfo>
              </Render>

              {/* 1. Card Alterações: Reposicionado abaixo do info card */}
              {!fullscreen && renderCardAlteracoes()}

              {/* 2. Overlay de aviso */}
              {renderOverlayAnalise()}

              {/* 3. Sidebar Fullscreen (Consolidado) */}
              <Render condition={fullscreen}>
                <CardFullscreen
                  pontosTotalMalha={points.length}
                  salvarPontosSelecionados={salvarPontosSelecionados}
                  adicionarPontosSelecionados={adicionarPontosSelecionados}
                  selecionarTodosPontos={selecionarTodosPontos}
                  deletarPontosSelecionados={deletarPontosSelecionados}
                  grupoAnalisesTratado={grupoAnalisesTratado}
                  pontos_selecionados_adicionados={pontos_selecionados_adicionados}
                  fake_loading={fake_loading}
                  toggleFullscreen={toggleFullscreen}
                  inserirPontos={inserirPontos}
                  moverPontos={moverPontos}
                  talhaoPath={talhaoPath}
                  pontos={points}
                  setInserirPontos={setInserirPontos}
                  setMoverPontos={setMoverPontos}
                  tipo_analise_selecionada={tipo_analise_selecionada}
                  setTipo_analise_selecionada={setTipo_analise_selecionada}
                  tipo_analise_selecionada_id={tipo_analise_selecionada_id}
                  setTipo_analise_selecionada_id={setTipo_analise_selecionada_id}
                  pontos_selecionados={pontos_selecionados}
                  setPontos_selecionados={setPontos_selecionados}
                  selecionarTodos={selecionarTodos}
                  setSelecionarTodos={setSelecionarTodos}
                  setPontos_selecionados_adicionados={setPontos_selecionados_adicionados}
                  cor={cor}
                  setCor={setCor}
                  grupoAnalises={grupoAnalises}
                />
              </Render>
            </GoogleMap>
          </div>
        </div>
      </ContainerPontosColeta>
    </ModalMalhaGeral>
  )
}
